import { Helmet } from 'react-helmet'
import { ReactNode } from 'react'
import classNames from 'classnames'
import PageTemplate from '../PageTemplate/PageTemplate'
import PageHeadings, { Crumb } from '../PageHeadings/PageHeadings'
import Label from '../Label'
import CollapsibleZone, { UniversalPageLayoutCollapsible } from './CollapsibleZone'

export type { UniversalPageLayoutCollapsible }

type UniversalPageLayoutBaseProps = {
	/** Zone 1 — mandatory. Passed straight through to PageHeadings. */
	heading: { title?: string; crumbs?: Crumb[] }
	/** Zone 1 — optional right-aligned context selector (period, project, portfolio). */
	primarySelector?: {
		/** Visually hidden; provides the accessible name for the control via the rendered <label>. */
		label: string
		/** id of the underlying control, so the rendered <label htmlFor> associates correctly. */
		controlId?: string
		children: ReactNode
	}
	/**
	 * Zone 1 — optional right-aligned view/granularity switcher (e.g. a Suppliers/Items radio
	 * group). Rendered alongside `primarySelector` rather than inside it: it changes what a row
	 * *means* (navigation), not which rows of a fixed shape are shown (filtering), so it belongs
	 * with the page-level context controls, not inside the collapsible Filters zone. Expects a
	 * self-labelling control (e.g. a fieldset/legend), not a single labelled input.
	 */
	viewSelector?: ReactNode
	/** Zone 1B — optional page-level messages (InlineAlerts), stacked above Zone 2 with the shared gap. */
	banners?: ReactNode
	/** Zone 2 — optional slot. Renders whatever is passed (e.g. EmissionsSummaryPanelModule) verbatim. */
	summary?: ReactNode
	/** Zone 2B — optional slot, rendered directly below `summary` (e.g. a TabMenu). */
	tabs?: ReactNode
	/** Zone 3A — simple inline controls (search/filter on left, actions on right). Renders in a single flex row, no collapsible header. */
	filtersSimple?: ReactNode
	/** Zone 3B — complex collapsible controls. Renders with optional toggle and H3 heading. */
	filtersComplex?: UniversalPageLayoutCollapsible
	/** Zone 4 — optional, right-justified action buttons. */
	actions?: ReactNode
	/** Zone 5 — mandatory main content. */
	children: ReactNode
	/** Zone 6 — optional, collapsible. */
	supplementary?: UniversalPageLayoutCollapsible
	/** Sets the browser tab title via react-helmet. Omit if the page renders its own <Helmet>. */
	documentTitle?: string
	/** Forwarded to PageTemplate's `testid`. */
	testid?: string
	additionalClass?: string
}

export type UniversalPageLayoutProps = UniversalPageLayoutBaseProps & (
	| { filtersSimple?: ReactNode; filtersComplex?: never }
	| { filtersComplex?: UniversalPageLayoutCollapsible; filtersSimple?: never }
	| { filtersSimple?: never; filtersComplex?: never }
)

const UniversalPageLayout = ({
	heading,
	primarySelector,
	viewSelector,
	banners,
	summary,
	tabs,
	filtersSimple,
	filtersComplex,
	actions,
	children,
	supplementary,
	documentTitle,
	testid,
	additionalClass,
}: UniversalPageLayoutProps) => {
	return (
		<PageTemplate testid={testid}>
			{documentTitle && (
				<Helmet>
					<title>{documentTitle}</title>
				</Helmet>
			)}
			{/* pt-3xl (25px) is flow spacing on this non-sticky container. It's larger than the visible 13px gap because the sticky box below cancels 12px of it via its own -mt-3 (net static offset = 25 - 12 = 13px), so the page still scrolls 13px before the heading reaches top-0 and sticks. */}
			<div className={classNames('pt-3xl', additionalClass)} data-testid='universal-page-layout'>
				{/* min-h matches the 42px control row height (search/select/radio) so Zone 1 is the same height whether or not a selector renders, keeping Zone 2 at a fixed vertical position across pages. -mt-3/pb-3 and -mx-4/px-4 bleed the background upward and horizontally without shifting the heading content or the scroll-before-stick distance. */}
				<div className='sticky top-0 z-10 flex gap-3xl items-start flex-wrap min-h-[42px] bg-charcoal-500 [.surface-dark_&]:bg-charcoal-700 -mx-4 px-4 -mt-3 pt-3 pb-3' data-testid='universal-page-layout-heading'>
					<PageHeadings title={heading.title} crumbs={heading.crumbs} />
					{(primarySelector || viewSelector) && (
						<div className='ml-auto flex items-end gap-x-5 flex-wrap self-end'>
							{viewSelector && (
								<div data-testid='universal-page-layout-view-selector'>{viewSelector}</div>
							)}
							{primarySelector && (
								<div data-testid='universal-page-layout-primary-selector'>
									<Label className='sr-only' htmlFor={primarySelector.controlId}>
										{primarySelector.label}
									</Label>
									{primarySelector.children}
								</div>
							)}
						</div>
					)}
				</div>

				{/*
				 * Zones 2-5 share a single flex-col gap so spacing between any two rendered zones is
				 * always consistent, regardless of which optional zones are present. Do not go back to
				 * per-zone `mt-*` margins here: a margin only added above an element provides no space
				 * below it, so whichever optional zone happens to render last before Zone 5 (Content)
				 * ends up flush against it - which is exactly the bug this structure fixes.
				 */}
				<div className='flex flex-col gap-5'>
					{banners && <div className='flex flex-col gap-md' data-testid='universal-page-layout-banners'>{banners}</div>}

					{summary && <div data-testid='universal-page-layout-summary'>{summary}</div>}

					{tabs && <div data-testid='universal-page-layout-tabs'>{tabs}</div>}

					{filtersComplex && (
						<CollapsibleZone {...filtersComplex} testIdPrefix='universal-page-layout-filters' headingLevel='h3' />
					)}

					{filtersSimple && (
					<div className='flex flex-wrap gap-y-4 gap-x-5 items-end w-full' data-testid='universal-page-layout-filters-simple'>
						{filtersSimple}
						{actions && (
							<div className='flex flex-wrap gap-x-5 ml-auto' data-testid='universal-page-layout-actions-inline'>
								{actions}
							</div>
						)}
					</div>
				)}

					{actions && !filtersSimple && (
						<div className='flex justify-end flex-wrap gap-md' data-testid='universal-page-layout-actions'>
							{actions}
						</div>
					)}

					<div className='rounded-lg overflow-hidden' data-testid='universal-page-layout-content'>{children}</div>
				</div>

				{supplementary && (
					<CollapsibleZone {...supplementary} testIdPrefix='universal-page-layout-supplementary' wrapperClass='mt-8' />
				)}
			</div>
		</PageTemplate>
	)
}

export default UniversalPageLayout
